import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { analytics } from '../utilities/analytics';
import { Text } from 'components/v2/Typography/Text';
import { loadGoogleAuth } from 'kb-redux/user.redux';
import { themes } from 'kb-shared';
import { BugTracker } from 'kb-shared/utilities/bugTracker';
import { isOnline } from 'utilities/browser';

type Props = {
  context?: 'signin' | 'signup';
};

const GOOGLE_SIGN_IN_SCRIPT = 'https://accounts.google.com/gsi/client';

export const GoogleAuthButton = (props: Props): JSX.Element => {
  const { context = 'signin' } = props;
  const dispatch = useDispatch();
  const [isInitialised, setIsInitialised] = useState<boolean | undefined>(undefined);
  const buttonContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if Google object already exists (script already loaded successfully)
    if (window?.google) {
      setIsInitialised(true);
      return;
    }

    // Get the script element from index.html
    const scriptElement = document.getElementById('google-sso-script');

    // Create a reference to the error handler function for proper cleanup
    const errorHandler = () => handleGoogleScriptError();

    // Add error event listener to the script element (CSP-friendly approach)
    if (scriptElement) {
      scriptElement.addEventListener('error', errorHandler);
    }

    // Set up a timeout to check if it loads within a reasonable time
    const timeoutId = setTimeout(() => {
      if (!window.google) {
        handleGoogleScriptError();
      }
    }, 5000);

    // Set up an interval to check if Google object becomes available
    const intervalId = setInterval(() => {
      if (window.google) {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
        setIsInitialised(true);
      }
    }, 100);

    // Clean up interval and timeout on component unmount
    return () => {
      if (scriptElement) {
        scriptElement.removeEventListener('error', errorHandler);
      }
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, []);

  const handleGoogleSignIn = useCallback(
    (response: CredentialResponse) => {
      if (!response || !response.credential) {
        handleGoogleSignInError();
        return;
      }

      analytics.track(analytics.EVENTS.GOOGLE_SIGN_IN_SUCCEEDED);
      dispatch(loadGoogleAuth(response.credential));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isInitialised || !window.google || !buttonContainerRef.current) return;

    try {
      window.google.accounts.id.cancel();

      window.google.accounts.id.initialize({
        client_id: import.meta.env.VITE_APP_GOOGLE_CLIENT_ID || '',
        callback: handleGoogleSignIn,
        ux_mode: 'popup',
        context: context,
        use_fedcm_for_button: true // https://developers.google.com/identity/gsi/web/reference/js-reference#use_fedcm_for_button
      });

      window.google.accounts.id.renderButton(buttonContainerRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'center',
        width: '320',
        type: 'standard'
      });
    } catch {
      BugTracker.notify('Google button render failed', 'GoogleAuthButton');
      setIsInitialised(false);
    }

    return () => {
      window.google?.accounts?.id?.cancel();
    };
  }, [isInitialised, context, handleGoogleSignIn]);

  const handleGoogleSignInError = () => {
    BugTracker.notify('Google Sign In Failed', 'GoogleAuthButton');
    analytics.track(analytics.EVENTS.GOOGLE_SIGN_IN_FAILED);
  };

  const handleGoogleScriptError = () => {
    if (!isOnline()) return;

    fetch(GOOGLE_SIGN_IN_SCRIPT)
      .then(() => {
        setIsInitialised(false);
        BugTracker.notify('Google SSO Load Error', 'Google SSO script load error');
      })
      .catch(error => {
        setIsInitialised(false);
        BugTracker.notify({
          name: 'Google SSO script fetch error',
          message: error.message,
          stack: error.stack,
          context: {
            endpoint: GOOGLE_SIGN_IN_SCRIPT,
            details: error?.details,
            userAgent: navigator.userAgent,
            online: navigator.onLine
          },
          tags: ['fetch', 'api'],
          component: 'GoogleAuthButton.tsx',
          action: 'handleGoogleScriptError'
        });
      });
  };

  const onButtonClicked = () =>
    context === 'signin'
      ? analytics.track(analytics.EVENTS.GOOGLE_SIGN_IN_STARTED)
      : analytics.track(analytics.EVENTS.SIGN_UP_STARTED_GOOGLE);

  const uninitialisedMessage = () => {
    return 'Google signin failed to load. Please refresh your browser to try loading it again.';
  };

  return (
    <>
      {isInitialised ? (
        <div ref={buttonContainerRef} onClick={onButtonClicked} />
      ) : (
        <Text color={themes.colors.semantic.darkRed}>{uninitialisedMessage()}</Text>
      )}
    </>
  );
};
